import Navbar from "@/components/Navbar";
import PageTransition from "@/components/PageTransition";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

export default function Legal() {
  const [host, setHost] = useState("stakeforge.io");
  useEffect(() => {
    if (typeof window !== "undefined" && window.location?.hostname) {
      const h = window.location.hostname;
      if (h && !["localhost", "127.0.0.1", ""].includes(h) && !/^\d+\.\d+\.\d+\.\d+$/.test(h)) {
        setHost(h);
      }
    }
  }, []);
  const supportEmail = `support@${host}`;
  const privacyEmail = `privacy@${host}`;
  const pressEmail   = `press@${host}`;
  const securityEmail= `security@${host}`;
  return (
    <PageTransition>
      <Navbar />
      <section className="max-w-4xl mx-auto px-6 pt-12 pb-24">
        <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ Legal</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">Legal & Disclosures</h1>
        <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--text-2)" }}>
          Last updated: February 12, 2026 · Stakeforge Labs SA, Zug — CHE-491.220.116
        </p>

        <nav className="border mt-10 grid grid-cols-2 md:grid-cols-5" style={{ borderColor: "var(--border)" }}>
          {[
            ["tos",      "Terms of Service"],
            ["privacy",  "Privacy"],
            ["risk",     "Risk Disclosure"],
            ["cookies",  "Cookies"],
            ["contact",  "Contact"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              data-testid={`legal-jump-${id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="px-4 py-3 text-xs uppercase tracking-widest border-r last:border-r-0 transition-colors text-left hover:text-white"
              style={{ borderColor: "var(--border)", color: "var(--text-2)", background: "transparent" }}
            >{label}</button>
          ))}
        </nav>

        <article className="legal-prose mt-16 space-y-14">
          <Section id="tos" num="01" title="Terms of Service">
            <p>By accessing Stakeforge (the "Platform") — including but not limited to <span className="font-mono">{host}</span>, the staking dApp, the standalone token pages, and any mirrors — you ("User") agree to the terms below. If you do not agree, do not use the Platform.</p>
            <ol>
              <li><strong>Self-custody.</strong> You retain full custody of any wallet you connect. Stakeforge never holds your private keys.</li>
              <li><strong>Eligibility.</strong> You must be at least 18 years old and not a resident of a Restricted Jurisdiction (see §3).</li>
              <li><strong>Restricted Jurisdictions.</strong> The Platform is unavailable to residents of the United States (OFAC-listed states), Iran, North Korea, Crimea, Donetsk, and Luhansk.</li>
              <li><strong>Validator-mesh delegations.</strong> Staked assets are programmatically delegated across our 34-validator partner mesh according to the Smoothing Pool ruleset.</li>
              <li><strong>No advice.</strong> Nothing on the Platform constitutes financial, legal, or tax advice.</li>
              <li><strong>Termination.</strong> We reserve the right to suspend access for breach of these Terms or applicable law.</li>
            </ol>
          </Section>

          <Section id="privacy" num="02" title="Privacy Policy">
            <p>We collect the minimum data required to operate the Platform.</p>
            <ul>
              <li><strong>What we collect:</strong> wallet address (when you connect), preference cookies, anonymous device/UA strings.</li>
              <li><strong>What we never collect:</strong> private keys, seed phrases, government IDs, biometric data.</li>
              <li><strong>Storage:</strong> session state and balance cache live in <span className="font-mono">localStorage</span> on your device.</li>
              <li><strong>Your rights (GDPR):</strong> access, rectification, erasure, restriction, portability, and objection. Email <a className="underline" href={`mailto:${privacyEmail}`}>{privacyEmail}</a>.</li>
            </ul>
          </Section>

          <Section id="risk" num="03" title="Risk Disclosure">
            <p>Staking carries real risk. By using the Platform you accept:</p>
            <ul>
              <li><strong>Smart-contract risk.</strong> Bugs, exploits, or governance attacks may result in partial or total loss of staked assets.</li>
              <li><strong>Slashing risk.</strong> Validators may misbehave and trigger protocol penalties. Stakeforge mitigates via the Insurance Vault (§4.3) but does not guarantee 100% recovery.</li>
              <li><strong>Market risk.</strong> Token prices are volatile. APR is denominated in the staked asset, not USD.</li>
              <li><strong>Lockup risk.</strong> Some pools impose 7–28 day unstake windows. Liquidity is not guaranteed during cooldown.</li>
              <li><strong>Daily Boost variability.</strong> 24h Boost pool yield depends on actual auction demand and may settle at zero on low-activity days.</li>
              <li><strong>Regulatory risk.</strong> Staking law is evolving. Tax treatment differs by jurisdiction.</li>
            </ul>
          </Section>

          <Section id="cookies" num="04" title="Cookies Policy">
            <p>We use only <strong>strictly necessary</strong> first-party storage:</p>
            <ul>
              <li><span className="font-mono">stk_wallet</span> — current connected wallet metadata</li>
              <li><span className="font-mono">stk_balances</span> — cached asset balances</li>
              <li><span className="font-mono">stk_positions</span> — active staking positions</li>
              <li><span className="font-mono">stk_history</span> — transaction log</li>
              <li><span className="font-mono">stk_referral</span>, <span className="font-mono">stk_leaderboard</span> — UI state</li>
            </ul>
            <p>No analytics, advertising, or tracking cookies are set.</p>
          </Section>

          <Section id="contact" num="05" title="Contact">
            <p>Stakeforge Labs SA<br />Gotthardstrasse 26, 6300 Zug, Switzerland</p>
            <p>Press: <a className="underline" href={`mailto:${pressEmail}`}>{pressEmail}</a> · Security: <a className="underline" href={`mailto:${securityEmail}`}>{securityEmail}</a> · Support: <a className="underline" href={`mailto:${supportEmail}`}>{supportEmail}</a></p>
          </Section>
        </article>

        <div className="mt-16 border-t pt-6 text-xs flex justify-between flex-wrap gap-2" style={{ borderColor: "var(--border)", color: "var(--text-3)" }}>
          <span>© 2026 Stakeforge Labs SA. All rights reserved.</span>
          <Link to="/" className="hover:text-white" data-testid="legal-home-link">← Back to platform</Link>
        </div>
      </section>
    </PageTransition>
  );
}

function Section({ id, num, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`legal-section-${id}`}>
      <div className="flex items-baseline gap-4 mb-5">
        <span className="font-mono text-[11px] tracking-[0.25em]" style={{ color: "var(--text-3)" }}>{num}</span>
        <h2 className="font-display font-extrabold text-3xl tracking-tight">{title}</h2>
      </div>
      <div className="space-y-4 text-sm" style={{ color: "var(--text-2)" }}>
        {children}
      </div>
    </section>
  );
}
